import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import {
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  LocalHospital as MedicalIcon,
  Wallet as WalletIcon,
  Analytics as AnalyticsIcon,
  LocalHospital as HospitalIcon,
  AccessTime as TimeIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DashboardService } from '../services/firebase';
import { HardcodedDashboardService } from '../services/hardcodedData';
import ExportDataModal from '../components/ExportDataModal';

// Helper function to create transparent colors
const getTransparentColor = (color: string, opacity: string) => `${color}${opacity}`;

// Define theme colors
const Colors = {
  primary: '#4A90E2',
  kbrBlue: '#4A90E2',
  kbrRed: '#EF4444',
  kbrGreen: '#10B981',
  kbrPurple: '#8B5CF6',
  background: '#f7fafc',
  white: '#ffffff',
  textPrimary: '#1A202C',
  textSecondary: '#718096',
  border: '#E2E8F0',
  confirmed: '#10B981',
  pending: '#F59E0B',
  completed: '#8B5CF6'
};

interface DashboardData {
  totalUsers: number;
  totalAppointments: number;
  totalRevenue: number;
  activeDoctors: number;
  todayAppointments: number;
  recentAppointments: Array<{
    id: string;
    patientName: string;
    doctor: string;
    time: string;
    status: string;
    avatar: string;
  }>;
  pendingInvoicesCount: number;
}

interface StatsCard {
  id: string;
  title: string;
  value: string;
  change: string;
  icon: React.ComponentType<{ sx?: any }>;
  backgroundColor: string;
  iconColor: string;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [exportModalOpen, setExportModalOpen] = useState(false);
  
  // Real-time listener cleanup function
  const [unsubscribe, setUnsubscribe] = useState<(() => void) | null>(null);
  
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    totalUsers: 0,
    totalAppointments: 0,
    totalRevenue: 0,
    activeDoctors: 0,
    todayAppointments: 0,
    recentAppointments: [],
    pendingInvoicesCount: 0
  });

  // Helper function to format currency properly
  const formatCurrency = (amount: number): string => {
    const numAmount = parseFloat(amount.toString()) || 0;
    
    if (isNaN(numAmount) || !isFinite(numAmount)) {
      return '₹0';
    }
    
    const roundedAmount = Math.round(numAmount * 100) / 100;
    return `₹${roundedAmount.toLocaleString('en-IN')}`;
  };

  // Set up real-time dashboard data
  useEffect(() => {
    const setupRealTimeData = async () => {
      try {
        console.log('🔄 Setting up real-time dashboard data...');
        setLoading(true);
        
        // Set up real-time listener directly - no hardcoded data
        const unsubscribeFn = DashboardService.subscribeToDashboardStats((stats) => {
          console.log('📊 Received dashboard stats update:', stats);
          
          const updatedData = {
            totalUsers: stats.totalUsers || 0,
            totalAppointments: stats.totalAppointments || 0,
            totalRevenue: stats.totalRevenue || 0,
            activeDoctors: stats.activeDoctors || 0,
            todayAppointments: stats.todayAppointments || 0,
            recentAppointments: stats.recentAppointments || [],
            pendingInvoicesCount: stats.pendingInvoicesCount || 0
          };
          
          console.log('📊 Setting dashboard data to:', updatedData);
          setDashboardData(updatedData);
          setConnectionError(false);
          setLoading(false);
        });
        
        setUnsubscribe(() => unsubscribeFn);
        
      } catch (error) {
        console.error('❌ Error setting up real-time dashboard:', error);
        setConnectionError(true);
        setLoading(false);
        setSnackbar({ 
          open: true, 
          message: 'Failed to connect to server', 
          severity: 'error' 
        });
      }
    };

    setupRealTimeData();

    // Cleanup function
    return () => {
      if (unsubscribe) {
        console.log('🧹 Cleaning up dashboard listeners...');
        unsubscribe();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manual refresh function
  const onRefresh = async () => {
    setIsRefreshing(true);
    try {
      console.log('🔄 Manual refresh triggered...');
      
      // Fetch fresh data from Firebase
      const stats = await DashboardService.getDashboardStats();
      
      setDashboardData({
        totalUsers: stats.totalUsers || 0,
        totalAppointments: stats.totalAppointments || 0,
        totalRevenue: stats.totalRevenue || 0,
        activeDoctors: stats.activeDoctors || 0,
        todayAppointments: stats.todayAppointments || 0,
        recentAppointments: stats.recentAppointments || [],
        pendingInvoicesCount: stats.pendingInvoicesCount || 0
      });
      
      setSnackbar({ open: true, message: 'Dashboard refreshed successfully!', severity: 'success' });
      console.log('✅ Dashboard refreshed manually');

    } catch (error) {
      console.error('❌ Error during dashboard refresh:', error);
      setSnackbar({ open: true, message: 'Failed to refresh dashboard', severity: 'error' });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        gap: 2
      }}>
        <CircularProgress size={48} />
        <Typography variant="body1" color="textSecondary">
          Loading dashboard...
        </Typography>
      </Box>
    );
  }

  // Stats cards configuration
  const statsCards: StatsCard[] = [
    {
      id: 'users',
      title: 'Total Users',
      value: dashboardData.totalUsers.toString(),
      change: `${dashboardData.totalUsers} registered`,
      icon: PeopleIcon,
      backgroundColor: '#E8F4FD',
      iconColor: Colors.kbrRed,
    },
    {
      id: 'appointments',
      title: 'Appointments',
      value: dashboardData.totalAppointments.toString(),
      change: `${dashboardData.todayAppointments} today`,
      icon: CalendarIcon,
      backgroundColor: '#FFF2E8',
      iconColor: Colors.kbrBlue,
    },
    {
      id: 'revenue',
      title: 'Revenue',
      value: formatCurrency(dashboardData.totalRevenue),
      change: 'Total collected',
      icon: TrendingUpIcon,
      backgroundColor: '#E8F5E8',
      iconColor: Colors.kbrGreen,
    },
    {
      id: 'doctors',
      title: 'Active Doctors',
      value: dashboardData.activeDoctors.toString(),
      change: 'Available today',
      icon: MedicalIcon,
      backgroundColor: '#F3E8FF',
      iconColor: Colors.kbrPurple,
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return Colors.confirmed;
      case 'pending': return Colors.pending;
      case 'scheduled': return Colors.confirmed;
      case 'completed': return Colors.completed;
      default: return Colors.textSecondary;
    }
  };

  const getStatusBackgroundColor = (status: string) => {
    switch (status) {
      case 'confirmed': return getTransparentColor(Colors.confirmed, '20');
      case 'pending': return getTransparentColor(Colors.pending, '20');
      case 'scheduled': return '#E8F5E8';
      case 'completed': return getTransparentColor(Colors.completed, '20');
      default: return getTransparentColor(Colors.textSecondary, '20');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: Colors.textPrimary, mb: 1 }}>
            KBR LIFE CARE HOSPITALS
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" sx={{ color: Colors.textSecondary }}>
              Admin Dashboard - Welcome back! Here's what's happening at your hospital today.
            </Typography>
            {connectionError && (
              <Chip 
                label="Using offline data" 
                size="small" 
                color="warning" 
                sx={{ fontSize: '10px' }}
              />
            )}
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={() => setExportModalOpen(true)}
          sx={{ 
            minWidth: 140,
            backgroundColor: Colors.kbrBlue,
            '&:hover': {
              backgroundColor: '#3A7BC8'
            }
          }}
        >
          Export Data
        </Button>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr 1fr' }, 
        gap: 3, 
        mb: 4 
      }}>
        {statsCards.map((card) => {
          const IconComponent = card.icon;
          return (
            <Card key={card.id} sx={{ 
              backgroundColor: card.backgroundColor,
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                    borderRadius: 2, 
                    p: 1.2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <IconComponent sx={{ fontSize: 24, color: card.iconColor }} />
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h2" sx={{ fontWeight: 'bold', fontSize: '32px', lineHeight: 1, color: Colors.textPrimary }}>
                      {card.value}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '12px', color: Colors.textSecondary, mt: 0.5 }}>
                      {card.id === 'appointments' ? `${dashboardData.todayAppointments} today` : card.change}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '13px', mb: 1, color: Colors.textPrimary }}>
                  {card.title}
                </Typography>
                {card.id === 'users' && (
                  <Chip 
                    label={`${card.value} registered`}
                    size="small" 
                    sx={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                      color: Colors.textPrimary, 
                      fontSize: '10px',
                      height: '20px'
                    }} 
                  />
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Quick Actions */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: Colors.textPrimary }}>
          Quick Actions
        </Typography>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, 
          gap: 3 
        }}>
          <Card 
            sx={{ 
              p: 3, 
              textAlign: 'center', 
              cursor: 'pointer',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 25px rgba(0,0,0,0.15)' },
              transition: 'all 0.3s ease',
              borderRadius: 3
            }}
            onClick={() => navigate('/services')}
          >
            <Box sx={{ 
              backgroundColor: '#4FC3F7', 
              borderRadius: 2, 
              p: 2, 
              display: 'inline-flex',
              color: 'white',
              mb: 2
            }}>
              <MedicalIcon sx={{ fontSize: 28 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '14px', mb: 0.5 }}>
              Manage Services
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>
              Add, edit, or remove services
            </Typography>
          </Card>

          <Card 
            sx={{ 
              p: 3, 
              textAlign: 'center', 
              cursor: 'pointer',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 25px rgba(0,0,0,0.15)' },
              transition: 'all 0.3s ease',
              borderRadius: 3
            }}
            onClick={() => navigate('/doctors')}
          >
            <Box sx={{ 
              backgroundColor: '#66BB6A', 
              borderRadius: 2, 
              p: 2, 
              display: 'inline-flex',
              color: 'white',
              mb: 2
            }}>
              <PeopleIcon sx={{ fontSize: 28 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '14px', mb: 0.5 }}>
              Manage Doctors
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>
              Add or assign doctors
            </Typography>
          </Card>

          <Card 
            sx={{ 
              p: 3, 
              textAlign: 'center', 
              cursor: 'pointer',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 25px rgba(0,0,0,0.15)' },
              transition: 'all 0.3s ease',
              borderRadius: 3
            }}
            onClick={() => navigate('/payments')}
          >
            <Box sx={{ 
              backgroundColor: '#4FC3F7', 
              borderRadius: 2, 
              p: 2, 
              display: 'inline-flex',
              color: 'white',
              mb: 2
            }}>
              <WalletIcon sx={{ fontSize: 28 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '14px', mb: 0.5 }}>
              Payment Management
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>
              Track payment transactions
            </Typography>
          </Card>

          <Card 
            sx={{ 
              p: 3, 
              textAlign: 'center', 
              cursor: 'pointer',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 25px rgba(0,0,0,0.15)' },
              transition: 'all 0.3s ease',
              borderRadius: 3
            }}
            onClick={() => navigate('/invoices')}
          >
            <Box sx={{ 
              backgroundColor: '#AB47BC', 
              borderRadius: 2, 
              p: 2, 
              display: 'inline-flex',
              color: 'white',
              mb: 2
            }}>
              <HospitalIcon sx={{ fontSize: 28 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '14px', mb: 0.5 }}>
              Invoice Management
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>
              Create & manage invoices
            </Typography>
          </Card>

          <Card 
            sx={{ 
              p: 3, 
              textAlign: 'center', 
              cursor: 'pointer',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 25px rgba(0,0,0,0.15)' },
              transition: 'all 0.3s ease',
              borderRadius: 3
            }}
            onClick={() => navigate('/appointments')}
          >
            <Box sx={{ 
              backgroundColor: '#FF6B95', 
              borderRadius: 2, 
              p: 2, 
              display: 'inline-flex',
              color: 'white',
              mb: 2
            }}>
              <CalendarIcon sx={{ fontSize: 28 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '14px', mb: 0.5 }}>
              View Appointments
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>
              Manage all appointments
            </Typography>
          </Card>

          <Card 
            sx={{ 
              p: 3, 
              textAlign: 'center', 
              cursor: 'pointer',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 25px rgba(0,0,0,0.15)' },
              transition: 'all 0.3s ease',
              borderRadius: 3
            }}
            onClick={() => navigate('/reports')}
          >
            <Box sx={{ 
              backgroundColor: '#FFA726', 
              borderRadius: 2, 
              p: 2, 
              display: 'inline-flex',
              color: 'white',
              mb: 2
            }}>
              <AnalyticsIcon sx={{ fontSize: 28 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '14px', mb: 0.5 }}>
              Hospital Reports
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>
              Analytics, revenue & insights
            </Typography>
          </Card>
        </Box>
      </Box>

      {/* Financial Overview and Recent Appointments */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 4 }}>
        {/* Financial Overview */}
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: Colors.textPrimary }}>
            Financial Overview
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Card 
              sx={{ 
                p: 3, 
                borderRadius: 3, 
                backgroundColor: '#E8F5E8',
                cursor: 'pointer', 
                '&:hover': { elevation: 4 }
              }}
              onClick={() => navigate('/payments')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#2E7D32', mb: 1 }}>
                    {formatCurrency(dashboardData.totalRevenue)}
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#1B5E20', fontWeight: 600 }}>
                    Total Revenue
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#388E3C', fontSize: '12px' }}>
                    All payments collected
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 48, color: '#4CAF50' }} />
              </Box>
            </Card>
            
            <Card 
              sx={{ 
                p: 3, 
                borderRadius: 3, 
                backgroundColor: '#FFF3E0',
                cursor: 'pointer', 
                '&:hover': { elevation: 4 }
              }}
              onClick={() => navigate('/appointments')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#E65100', mb: 1 }}>
                    {dashboardData.pendingInvoicesCount}
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#BF360C', fontWeight: 600 }}>
                    Pending Appointments
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#D84315', fontSize: '12px' }}>
                    Need confirmation
                  </Typography>
                </Box>
                <TimeIcon sx={{ fontSize: 48, color: '#FF9800' }} />
              </Box>
            </Card>
          </Box>
        </Box>

        {/* Recent Appointments */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: Colors.textPrimary }}>
              Recent Appointments
            </Typography>
            <Button 
              variant="text" 
              sx={{ color: '#1976D2', fontWeight: 600, textTransform: 'none' }}
              onClick={() => navigate('/appointments')}
            >
              View All
            </Button>
          </Box>
          
          <Box>
            {dashboardData.recentAppointments.length > 0 ? (
              dashboardData.recentAppointments.map((appointment, index) => (
                <Card key={appointment.id} sx={{ p: 2.5, mb: 2, borderRadius: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ 
                        bgcolor: index === 0 ? '#FF5722' : index === 1 ? '#FF5722' : '#2196F3', 
                        width: 45, 
                        height: 45, 
                        fontSize: '16px', 
                        fontWeight: 'bold' 
                      }}>
                        {appointment.avatar}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                          {appointment.patientName}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>
                          {appointment.doctor} • {appointment.time}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip 
                      label={appointment.status}
                      size="small" 
                      sx={{ 
                        backgroundColor: getStatusBackgroundColor(appointment.status), 
                        color: getStatusColor(appointment.status), 
                        fontWeight: 'bold',
                        fontSize: '11px'
                      }} 
                    />
                  </Box>
                </Card>
              ))
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CalendarIcon sx={{ fontSize: 48, color: Colors.textSecondary, mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: Colors.textSecondary, mb: 1 }}>
                  No appointments yet
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Appointments will appear here once patients start booking
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Export Data Modal */}
      <ExportDataModal 
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
      />
    </Box>
  );
};

export default Dashboard;